import { Fragment, useCallback, useEffect, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import BSForm from "react-bootstrap/Form";
import { IoIosArrowDown } from "react-icons/io";
import { BsPlusLg } from "react-icons/bs";
import styled from "styled-components";
import AnimateHeight from "react-animate-height";
import {
	CustomButton,
	CustomSelect,
	CustomNumberInput,
	FormikFieldError,
	CustomInput,
} from "components/CustomForm";
import PersianPickerTile from "components/PersianPickerTile";
import { createProductBuy, updateProductBuy, getProductStatistics } from "network/apis/BuyApi";
import {
	getBuysReport,
	getProvinceExcelReport,
} from 'network/apis/ReportingApi';
import { getCorporations } from 'network/apis/CorporationApi';
import {
	getUnits,
	getIngredients,
	IN_STOCK,
	OUT_OF_STOCK,
} from "network/apis/CommonApi";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { ErrorMessage, Field, Formik, Form } from "formik";
import { isBlank, getErrorMessage } from "helpers/strings";
import { getFormattedDate } from "helpers/dates";
import { openLink } from 'helpers/general';
import { useProvinceSelector, useProvinceDispatch } from 'province-app/redux/province.store';
import {
	handleDateRadioChange,
	pickFromDate,
	pickToDate,
	handleInputChange,
	handleCompanyChange,
	handleFilterTypeChange,
	goToPage,
	buildFiltersQuery,
	DateRadio,
	DefaultConcentrateFilter,
	DefaultInputFilter,
	CompFilterType,
	FilterTypeOptions,
} from "province-app/redux/filter.slice";
import { AllCompsFilterType } from "province-app/redux/filter.slice";
import Skeleton from "react-loading-skeleton";
import { NumericFormat } from "react-number-format";
import PersianPickerInput from "components/PersianPickerInput";
import useScreen from 'hooks/useScreen';

const Check = styled(BSForm.Check)`
  display: inline;
`;

const ResultBox = styled.div`
  border: 1px solid black;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
`;

const ToggleCollapse = styled.span`
  background-color: #784af6;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 1rem;
  cursor: pointer;
  position: absolute;
  bottom: -1rem;
  transform: rotate(${(props) => (props.open ? "180deg" : "0")});

  transition: all 300ms ease-in-out;
`;

const FormLabel = styled.label`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ProductsSelect = styled(CustomSelect)`
    width: 50%;
`;

const Holder = styled.div`
  display: flex;
  column-gap: 12px;
  justify-content: space-between;
  padding: 1.7rem 0 1.5rem 1.1rem;
  direction: rtl;

  & > *:not(button) {
	flex: 1 1 0;
	width: 0;
  }

  @media (max-width: 900px) {
	flex-direction: column;
	row-gap: 1rem;

	& > *:not(button) {
		flex: 1;
		width: inherit;
	  }
  }
`;

const HDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #c6c6c6;
  margin-top: 2rem;
`;

const BuyTabMenu = ({ tabData, collapsed, onCollapse, onCreateSuccess }) => {
	const [currentData, setCurrentData] = useState();
	const isEditMode = useCallback(
		() => currentData && currentData != null,
		[currentData]
	);

	const [loading, setLoading] = useState(false);
	const [selectedIngredient, setSelectedIngredient] = useState();
	const [selectedUnit, setSelectedUnit] = useState();
	const [selectedDate, setSelectedDate] = useState(getFormattedDate());
	const [selectedStorageLocation, setSelectedStorageLocation] = useState();
	const [collapseHeight, setCollapseHeight] = useState(0);
	const [formikKey, setFormikKey] = useState(1);

	const filterState = useProvinceSelector((store) => store.filter);
	const dispatch = useProvinceDispatch();

	const { size, lessThan1200, lessThan900, lessThan768 } = useScreen();

	useEffect(() => {
		setCurrentData(tabData);
	}, [tabData]);

	useEffect(() => {
		const isEditMode = currentData && currentData != null;

		setCollapseHeight(isEditMode ? "auto" : 0);

		setSelectedIngredient(
			isEditMode
				? { label: currentData.input.name, value: currentData.input.input_id }
				: null
		);
		setSelectedUnit(
			isEditMode
				? { label: currentData.unit.name, value: currentData.unit.unit_id }
				: null
		);
		setSelectedStorageLocation(
			isEditMode
				? {
					label:
						currentData.storage_location === IN_STOCK
							? "موجود در انبار"
							: "سایر",
					value: currentData.storage_location,
				}
				: null
		);
		if (isEditMode && currentData?.date) {
			setSelectedDate(currentData?.date);
		}

		setFormikKey((prev) => prev + 1);
	}, [currentData, isEditMode]);

	const { data: statisticsData, isLoading: statisticsDataIsLoading } = useQuery(
		["province-get-product-statistics", filterState.filterQuery],
		() => getProductStatistics(filterState.filterQuery),
		{
			refetchOnWindowFocus: false,
		}
	);

	const { data: inputsData } = useQuery(
		["get-ingredients"],
		() => getIngredients(),
		{
			refetchOnWindowFocus: false,
		}
	);

	const { data: unitsData } = useQuery(["get-units"], () => getUnits(), {
		refetchOnWindowFocus: false,
	});

	const addMutation = useMutation(
		isEditMode() ? updateProductBuy : createProductBuy,
		{
			onSuccess: (res) => {
				if (res && res.data) {
					toast(`با موفقیت ${isEditMode() ? "ویرایش" : "ایجاد"} گردید`, {
						type: "success",
						position: "bottom-right",
					});
					onCreateSuccess?.();
					onToggle();
				}
			},
			onError: (err) => {
				toast(getErrorMessage(err), {
					type: "error",
					position: "bottom-right",
				});
			},
			onSettled: () => {
				setLoading(false);
			},
		}
	);

	const resetInputs = () => {
		setCurrentData(null);
		setSelectedIngredient(null);
		setSelectedUnit(null);
		setSelectedDate(getFormattedDate());
		setSelectedStorageLocation(null);
		setFormikKey((prev) => prev + 1);
	};

	const getInputOtions = () => {
		if (inputsData?.data) {
			const inputOptions = inputsData.data.map((input, index) => {
				return { value: input?.item_id, label: input?.name };
			});

			return [DefaultConcentrateFilter, ...inputOptions];
		}

		return [];
	};

	const { data: corporationsData } = useQuery(["get-corporations"], () => getCorporations(), {
		refetchOnWindowFocus: false,
	});
	const getCorporationsOptions = () => {
		if (corporationsData?.data) {
			const compsOptions = corporationsData.data.map((corporation, index) => {
				return { value: corporation?.coop_id, label: corporation?.name };
			});

			return [CompFilterType, ...compsOptions];
		}

		return [];
	};

	const getUnitOptions = () =>
		unitsData?.data
			? unitsData.data.map((unit, index) => {
				return { value: unit?.unit_id, label: unit?.name };
			})
			: [];

	const getStorageLocationOptions = () => [
		{ value: IN_STOCK, label: "موجود در انبار" },
		{ value: OUT_OF_STOCK, label: "سایر" },
	];

	const onToggle = () => {
		if (collapseHeight === "auto") resetInputs();
		onCollapse?.(collapseHeight === 0);
		setCollapseHeight(collapseHeight === 0 ? "auto" : 0);
	};

	const onInputChange = (newVal) => {
		setSelectedIngredient(newVal);

		const targetInput = inputsData?.data?.find((i) => i.item_id);
		if (targetInput) {
			const defaultUnit = {
				value: targetInput.default_unit?.unit_id,
				label: targetInput.default_unit?.name
			};
			setSelectedUnit(defaultUnit);
		}
	};

	const { refetch: fetchTableExcel } = useQuery(
		["get-table-excel", filterState.filterQuery],
		() => getBuysReport(filterState.filterQuery + 'excel=true'),
		{
			refetchOnWindowFocus: false,
			enabled: false,
			onSuccess: (data) => {
				const url = data?.list?.data?.url;
				if (url) openLink(url);
			},
		}
	);

	const { refetch: fetchProvinceExcel } = useQuery(
		["get-province-excel", filterState.filterQuery],
		() => getProvinceExcelReport('purchase', '&' + filterState.filterQuery),
		{
			refetchOnWindowFocus: false,
			enabled: false,
			onSuccess: (data) => {
				const url = data?.data;
				if (url) openLink(url);
			},
		}
	);

	const downloadTableExcel = () => {
		fetchTableExcel();
	};

	const downloadProvinceExcel = () => {
		fetchProvinceExcel();
	};

	const isCompanyFilterSelected = () => (filterState.selectedFilterType?.value ?? -1) === 0;

	const getClassNameForFiltersRow = () => {
		if (lessThan900) {
			return 'mt-3 d-flex justify-content-between align-items-center flex-wrap-reverse';
		} else {
			const startPaddings = isCompanyFilterSelected() ? 'ms-1 ps-1' : 'ms-5 ps-5';
			return `${startPaddings} mt-3 d-flex justify-content-between align-items-center flex-wrap-reverse`;
		}
	};

	const dispatchFilters = () => {
		dispatch(goToPage(1));
		dispatch(buildFiltersQuery());
	};

	return (
		<Fragment>
			<Col
				md={lessThan768 ? 12 : 4}
				className={`d-flex align-items-center justify-content-${lessThan768 ? 'center mb-4' : 'end'}`}
			>
				<div className='mx-5 d-flex flex-column align-items-center gap-2'>
					<CustomButton
						fontSize={10}
						onClick={downloadProvinceExcel}
						color='#0278EE'
						className='w-100'
					>
						اکسل شرکت ها
					</CustomButton>
					<CustomButton
						fontSize={10}
						onClick={downloadTableExcel}
						color='#0278EE'
						className='w-100'
					>
						اکسل جدول
					</CustomButton>
				</div>
				<ResultBox className="w-100">
					{statisticsDataIsLoading ? (
						<Skeleton count={1} width={34} height={20} />
					) : (
						<span>{statisticsData?.data?.unit?.name}</span>
					)}
					{statisticsDataIsLoading ? (
						<Skeleton count={1} width={60} height={20} />
					) : (
						<span>{`${statisticsData?.data?.sum ?? 0}`.persianCurrency()}</span>
					)}
					<span>:مجموع</span>
				</ResultBox>
			</Col>
			<Col md={lessThan768 ? 12 : 8}>
				<div
					className={`${lessThan900 ? 'ps-4' : 'ms-5 ps-5'} d-flex justify-content-between align-items-center`}
				>
					<div className="d-inline-flex flex-wrap align-items-center">
						<PersianPickerTile
							currentDate={filterState.pickedDateTo}
							topOffset={lessThan1200 ? '36%' : '18%'}
							leftOffset={lessThan1200 ? '3%' : '37%'}
							onPick={(pickedDate) => dispatch(pickToDate(pickedDate))}
						/>
						<span className="mx-2">تا</span>
						<PersianPickerTile
							currentDate={filterState.pickedDateFrom}
							topOffset={lessThan1200 ? '36%' : '18%'}
							leftOffset={lessThan1200 ? '11%' : '49%'}
							onPick={(pickedDate) => dispatch(pickFromDate(pickedDate))}
						/>
					</div>
					<div className="d-inline-flex flex-wrap justify-content-end">
						<FormLabel htmlFor={DateRadio.CustomDate}>بازه زمانی دلخواه</FormLabel>
						<Check
							className={`${lessThan1200 ? '' : 'me-3'} ms-2`}
							type="radio"
							id={DateRadio.CustomDate}
							checked={filterState.selectedRadio === DateRadio.CustomDate}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
						<FormLabel
							htmlFor={DateRadio.LastMonth}
							className={`${size.width < 560 ? 'mt-3' : ''}`}
						>
							خرید های یک ماه گذشته
						</FormLabel>
						<Check
							className={`ms-1 ${size.width < 560 ? 'mt-3' : ''}`}
							type="radio"
							id={DateRadio.LastMonth}
							checked={filterState.selectedRadio === DateRadio.LastMonth}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
				</div>
				<div className={getClassNameForFiltersRow()}>
					<CustomButton
						className={`${lessThan900 ? 'w-100 mt-3' : ''}`}
						onClick={dispatchFilters}
					>
						نمایش
					</CustomButton>
					<div className='d-inline-flex align-items-center justify-content-end flex-grow-2'>
						{isCompanyFilterSelected() && (
							<CustomSelect
								placeholder='انتخاب شرکت'
								className={`ms-2 w-50`}
								radius="14"
								options={getCorporationsOptions()}
								onChange={(newCorporation) => dispatch(handleCompanyChange(newCorporation))}
								value={filterState.selectedCompany}
								menuPosition='fixed'
							/>
						)}
						<ProductsSelect
							placeholder='همه شرکت ها'
							className="w-50 ms-3 me-3"
							radius="18"
							options={[...FilterTypeOptions]}
							onChange={(newFilter) => dispatch(handleFilterTypeChange(newFilter))}
							value={filterState.selectedFilterType}
							menuPosition='fixed'
						/>
						<ProductsSelect
							className="w-50 me-4"
							radius="18"
							options={getInputOtions()}
							onChange={(newInput) => dispatch(handleInputChange(newInput))}
							value={filterState.selectedInput}
							menuPosition='fixed'
						/>
						<FormLabel htmlFor={DateRadio.LastThreeMonths} className='text-end flex-shrink-0'>
							خرید های سه ماه گذشته
						</FormLabel>
						<Check
							className="ms-2"
							type="radio"
							id={DateRadio.LastThreeMonths}
							checked={filterState.selectedRadio === DateRadio.LastThreeMonths}
							onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
						/>
					</div>
				</div>
			</Col>
			<ToggleCollapse
				onClick={() => onToggle()}
				open={collapseHeight === "auto"}
			>
				<div>
					<IoIosArrowDown size={22} />
				</div>
			</ToggleCollapse>
			<AnimateHeight height={collapseHeight} duration={500}>
				<HDivider />
				<Formik
					key={formikKey}
					initialValues={{
						amount: `${currentData?.amount ?? ""}`,
						cost: `${currentData?.cost ?? ""}`,
						description: currentData?.description ?? '',
					}}
					validate={(values) => {
						const errors = {};
						if (!selectedIngredient) {
							errors.input_id = "ضروری";
						} else if (isBlank(values.amount)) {
							errors.amount = "ضروری";
						} else if (isBlank(values.cost)) {
							errors.cost = "ضروری";
						} else if (!selectedUnit) {
							errors.unit_id = "ضروری";
						} else if (!selectedStorageLocation) {
							errors.storage_location = "ضروری";
						} else if (isBlank(values.description)) {
							if (selectedStorageLocation && selectedStorageLocation.value === OUT_OF_STOCK) {
								errors.description = "ضروری";
							}
						}
						return errors;
					}}
					onSubmit={(values) => {
						setLoading(true);

						addMutation.mutate({
							id: currentData?.pur_id,
							input_id: selectedIngredient?.value,
							date: selectedDate,
							unit_id: selectedUnit?.value,
							storage_location: selectedStorageLocation?.value,
							...values,
						});
					}}
				>
					{({ submitForm }) => (
						<Form>
							<Holder>
								<div>
									<Field type="text" name="input_id">
										{({ field, meta: { touched, error } }) => (
											<CustomSelect
												className={`order-1 ${touched && error ? "invalid" : ""}`}
												error={!isBlank(error)}
												{...field}
												placeholder="نوع نهاده"
												radius="14"
												options={getInputOtions()}
												onChange={onInputChange}
												value={selectedIngredient}
												isDisabled={isEditMode()}
											/>
										)}
									</Field>
									<ErrorMessage name="input_id" component={FormikFieldError} />
								</div>
								<div>
									<Field type="text" name="amount">
										{({ field, meta: { touched, error } }) => (
											<CustomNumberInput
												className={`order-1 ${touched && error ? "invalid" : ""}`}
												error={!isBlank(error)}
												{...field}
												placeholder="مقدار"
												radius="14"
											/>
										)}
									</Field>
									<ErrorMessage name="amount" component={FormikFieldError} />
								</div>
								<div>
									<Field type="text" name="unit_id">
										{({ field, meta: { touched, error } }) => (
											<CustomSelect
												className={touched && error ? "invalid" : ""}
												error={!isBlank(error)}
												{...field}
												placeholder="واحد"
												radius="14"
												options={getUnitOptions()}
												onChange={setSelectedUnit}
												value={selectedUnit}
												isDisabled={isEditMode()}
											/>
										)}
									</Field>
									<ErrorMessage name="unit_id" component={FormikFieldError} />
								</div>
								<div>
									<Field type="number" name="cost">
										{({ field, meta: { touched, error } }) => (
											<CustomNumberInput
												className={`order-2 ${touched && error ? "invalid" : ""}`}
												error={!isBlank(error)}
												{...field}
												placeholder="مبلغ (ریال)"
												radius="14"
											/>
										)}
									</Field>
									<ErrorMessage name="cost" component={FormikFieldError} />
								</div>
								<div className="grow-2">
									<Field name="date">
										{({ field, meta: { touched, error } }) => (
											<PersianPickerInput
												currentDate={selectedDate}
												topOffset={90}
												leftOffset={29}
												key={selectedDate ?? 'e'}
												onPick={setSelectedDate}
												label='تاریخ خرید'
												field={field}
												touched={touched}
												error={error}
											/>
										)}
									</Field>
									<ErrorMessage name="date" component={FormikFieldError} />
								</div>
								<div>
									<Field type="text" name="storage_location">
										{({ field, meta: { touched, error } }) => (
											<CustomSelect
												className={touched && error ? "invalid" : ""}
												error={!isBlank(error)}
												{...field}
												placeholder="محل نگهداری"
												radius="14"
												options={getStorageLocationOptions()}
												onChange={setSelectedStorageLocation}
												value={selectedStorageLocation}
											/>
										)}
									</Field>
									<ErrorMessage
										name="storage_location"
										component={FormikFieldError}
									/>
								</div>
								<div>
									<Field type="text" name="description">
										{({ field, meta: { touched, error } }) => (
											<CustomInput
												className={touched && error ? "invalid" : ""}
												error={!isBlank(error)}
												{...field}
												placeholder="توضیحات"
												radius="14"
											/>
										)}
									</Field>
									<ErrorMessage
										name="description"
										component={FormikFieldError}
									/>
								</div>
								<CustomButton
									onClick={submitForm}
									disabled={loading}
									className="d-flex align-items-center justify-content-center"
								>
									{loading ? (
										<Spinner animation="border" role="status" size="sm" />
									) : (
										<span>{isEditMode() ? "ویرایش" : "ثبت"}</span>
									)}
								</CustomButton>
							</Holder>
						</Form>
					)}
				</Formik>
			</AnimateHeight>
		</Fragment>
	);
};

export default BuyTabMenu;
